<template>
  <b-modal ref="modal" size="lg" title="Create operator from template" hide-footer
    :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy" @hide="guardClose">
    <b-alert v-if="error" show variant="danger" role="alert">{{ error }}</b-alert>
    <b-button v-if="error && preview && !submitted && !busy" variant="outline-secondary" class="mb-3" @click="loadPreview">
      <i class="fas fa-sync" aria-hidden="true" /> Refresh template
    </b-button>
    <div v-if="loading" role="status"><b-spinner small /> Loading template</div>
    <form v-else-if="preview" @submit.prevent="submit">
      <div class="mb-3">
        <strong>Template: {{ preview.sourceName }}</strong>
        <span class="ml-2">{{ preview.limitCount }} limit configurations</span>
      </div>
      <b-table small responsive sticky-header="240px" :items="preview.limits" :fields="limitFields" :per-page="20" :current-page="previewPage" show-empty empty-text="No configured limits" />
      <b-pagination v-if="preview.limits.length > 20" v-model="previewPage" :total-rows="preview.limits.length" :per-page="20" size="sm" align="right" aria-label="Template limits pages" />
      <fieldset :disabled="busy || submitted">
        <b-row>
          <b-col cols="12" md="8">
            <b-form-group label="New operator name" label-for="template-name">
              <b-form-input id="template-name" v-model.trim="form.name" required minlength="3" maxlength="40" autocomplete="off" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="API version" label-for="template-version">
              <b-form-input id="template-version" v-model.number="form.apiVersion" type="number" min="0.1" step="any" required />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col v-for="field in urls" :key="field.id" cols="12" md="6">
            <b-form-group :label="field.label" :label-for="'template-' + field.id">
              <b-form-input :id="'template-' + field.id" v-model.trim="form[field.id]" type="url" :required="field.required" maxlength="2048" autocomplete="off" />
            </b-form-group>
          </b-col>
        </b-row>
      </fieldset>
      <div v-if="operation" class="mb-3 template-operation" role="status">
        Operation: <code>{{ operation.operationId }}</code><br>
        Status: {{ operation.state }}
      </div>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" :disabled="busy" @click="$refs.modal.hide()">Close</b-button>
        <b-button variant="primary" type="submit" :disabled="busy">
          <b-spinner v-if="busy" small />
          <i v-else :class="submitted ? 'fas fa-redo' : 'fas fa-copy'" aria-hidden="true" />
          {{ submitted ? 'Resume operation' : 'Create operator' }}
        </b-button>
      </div>
    </form>
    <b-button v-else-if="!loading" variant="secondary" @click="loadPreview">Retry</b-button>
  </b-modal>
</template>

<script>
const emptyForm = () => ({
  name: '', apiVersion: 1.3,
  creditUrl: '', debitUrl: '', getBalanceUrl: '', rollbackUrl: '', authUrl: '', homeUrl: '',
});

function operationKey() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default {
  data: () => ({
    sourceId: null, sourceType: 'operators', preview: null, previewPage: 1, loading: false, busy: false, error: '',
    form: emptyForm(), key: null, submitted: false, operation: null,
    limitFields: [
      { key: 'tableId', label: 'Table' }, { key: 'currency', label: 'Currency' },
      { key: 'limitType', label: 'Type' }, { key: 'limitName', label: 'Name' },
      { key: 'hasCalculation', label: 'Calculation profile' },
    ],
    urls: [
      { id: 'debitUrl', label: 'Debit URL', required: true },
      { id: 'creditUrl', label: 'Credit URL', required: true },
      { id: 'getBalanceUrl', label: 'Balance URL', required: true },
      { id: 'rollbackUrl', label: 'Rollback URL', required: true },
      { id: 'authUrl', label: 'Auth URL', required: false },
      { id: 'homeUrl', label: 'Home URL', required: false },
    ],
  }),
  methods: {
    show(sourceId, sourceType = 'operators') {
      // Accept only the source ID, never the operator DTO or its settings/credentials.
      if (this.busy) return;
      this.sourceId = sourceId;
      this.sourceType = sourceType === 'engine-operators' ? sourceType : 'operators';
      this.preview = null;
      this.previewPage = 1;
      this.form = emptyForm();
      this.key = operationKey();
      this.submitted = false;
      this.operation = null;
      this.error = '';
      this.$refs.modal.show();
      this.loadPreview();
    },
    async loadPreview() {
      const sourceId = this.sourceId;
      const sourceType = this.sourceType;
      this.loading = true;
      this.error = '';
      try {
        const response = await this.$http.get(`/api/v1/${sourceType}/${sourceId}/template`);
        if (this.sourceId === sourceId && this.sourceType === sourceType) {
          if (!this.preview) {
            const defaults = response.data.formDefaults || {};
            for (const field of ['apiVersion', ...this.urls.map(item => item.id)]) {
              if (Object.prototype.hasOwnProperty.call(defaults, field)) this.form[field] = defaults[field];
            }
          }
          this.preview = response.data;
          this.previewPage = 1;
        }
      } catch (error) {
        if (this.sourceId === sourceId && this.sourceType === sourceType) this.error = this.message(error);
      } finally {
        if (this.sourceId === sourceId && this.sourceType === sourceType) this.loading = false;
      }
    },
    async submit() {
      if (this.busy || !this.preview) return;
      this.busy = true;
      this.error = '';
      try {
        let response;
        if (this.operation) {
          response = await this.$http.post(`/api/v1/operators/template-operations/${this.key}/resume`);
        } else {
          response = await this.$http.post(`/api/v1/${this.sourceType}/${this.sourceId}/from-template`, {
            ...this.form, revision: this.preview.revision,
          }, { headers: { 'Idempotency-Key': this.key } });
        }
        this.operation = response.data;
        this.$emit('finish');
        if (this.operation.state === 'complete') {
          this.$toastr.s('Operator created from template');
          this.busy = false;
          this.$refs.modal.hide();
        }
      } catch (error) {
        const data = error.response && error.response.data;
        if (data && data.operationId) this.operation = data;
        // Ambiguous transport outcomes retain exactly the same key and form payload.
        this.submitted = !!this.operation || !error.response || error.response.status >= 500;
        if (data && data.state === 'rejected') {
          this.operation = null;
          this.key = operationKey();
          this.submitted = false;
        }
        this.error = this.message(error);
        this.$emit('finish');
      } finally {
        this.busy = false;
      }
    },
    message(error) {
      const data = error.response && error.response.data;
      return (data && (data.message || data.detail)) || 'Request failed. Retry the same operation.';
    },
    guardClose(event) {
      if (this.busy) event.preventDefault();
    },
  },
};
</script>

<style scoped>
.template-operation { overflow-wrap: anywhere; }
</style>
